import { useState } from 'react'

import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import { invalidateFetch } from '@/reducers/fetch'
import {
  FETCH_SUBSCRIPTIONS_KEY,
  fetchSubscriptions,
} from '@/reducers/subscription.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useCardSummary } from '@/features/billing/payment-method/useCardSummary'
import { usePaymentMethodUpdate } from '@/features/billing/payment-method/usePaymentMethodUpdate'
import { PaymentMethodForm } from '@/features/billing/payment-method/PaymentMethodForm'
import { savedCopy } from '@/features/billing/payment-method/paymentMethodCopy'
import type { CardSummary } from '@/features/billing/payment-method/payment-method.types'

import { Alert, Sheet, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

interface Props {
  daysUntilSuspension: number
}

export const AccountSuspendedBanner = ({ daysUntilSuspension }: Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [currentCard, setCurrentCard] = useState<CardSummary | null>(null)
  const [savedMessage, setSavedMessage] = useState('')

  const dispatch = useAppDispatch()
  const loadCardSummary = useCardSummary()
  const update = usePaymentMethodUpdate({
    surface: 'suspended banner',
    onSaved: (card) => {
      setSavedMessage(savedCopy(card))
      dispatch(fetchCurrentUserDetailsIfNeeded(true))
      // The banner reads subscription status, and fetchSubscriptions skips a key that already holds a receivedAt.
      dispatch(invalidateFetch(FETCH_SUBSCRIPTIONS_KEY))
      dispatch(fetchSubscriptions())
      setSheetOpen(false)
    },
  })
  const saving = update.status === 'saving'

  const closeSheet = (): void => {
    update.cancel()
    setSheetOpen(false)
  }

  const onCta = (): void => {
    setSavedMessage('')
    void loadCardSummary().then(setCurrentCard)
    setSheetOpen(true)
    update.open()
  }

  return (
    <>
      <Alert
        variant="error"
        title="Your last payment for Heard has failed."
        ctaText="Update Payment"
        ctaOnClick={onCta}
      >
        <Text variant={TypographyStyles.BODY_SM}>
          {`Please update it now or your account will be suspended in ${daysUntilSuspension === 0 ? '<1' : daysUntilSuspension} days. If you have recently updated your payment information, it may take a minute for these changes to reflect.`}
        </Text>
      </Alert>
      <Text
        role="status"
        variant={TypographyStyles.BODY_SM}
        className={savedMessage ? undefined : 'sr-only'}
      >
        {savedMessage}
      </Text>
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open && !saving) closeSheet()
        }}
      >
        <Sheet.Content
          side="right"
          aria-describedby={undefined}
          dismissible={!saving}
          onEscapeKeyDown={(event) => {
            if (saving) event.preventDefault()
          }}
          onInteractOutside={(event) => {
            if (saving) event.preventDefault()
          }}
          className="flex flex-col gap-4 overflow-y-auto px-8 py-8 lg:w-[560px]"
        >
          <Sheet.Title>Update payment method</Sheet.Title>
          <PaymentMethodForm
            update={update}
            flow="account"
            currentCard={currentCard}
            onCancel={closeSheet}
          />
        </Sheet.Content>
      </Sheet>
    </>
  )
}
